import { Check, ChevronDown, Loader2, Plus } from "lucide-react";
import { useState } from "react";

import { Avatar, AvatarFallback } from "@/common/ui/avatar";
import { Button } from "@/common/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/common/ui/dropdown-menu";

import { useSession } from "../useSession";
import { CreateOrgModal } from "./CreateOrgModal";

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function OrgSwitcher(): React.ReactElement {
  const { session, isLoading, isSwitching, switchOrg, refreshSession } = useSession();
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const activeOrgUnit = session?.activeOrgUnit;
  const memberships = session?.memberships ?? [];

  const handleSwitch = async (orgUnitId: string): Promise<void> => {
    if (orgUnitId === activeOrgUnit?.id) return;
    try {
      await switchOrg(orgUnitId);
    } catch {
      // switchOrg handles the API error; catch here to prevent unhandled rejection
    }
  };

  // Render nothing while the session is still resolving or if no active
  // org is set. A skeleton chip in a single-element header reads as a
  // perpetual "loading" indicator if the session call never settles —
  // better to just appear when ready.
  if (isLoading || !activeOrgUnit) return <></>;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-2 px-2" data-testid="org-switcher-trigger">
            {isSwitching ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <Avatar className="h-6 w-6">
                <AvatarFallback className="bg-primary text-[10px] text-primary-foreground">
                  {getInitials(activeOrgUnit.name)}
                </AvatarFallback>
              </Avatar>
            )}
            <span className="hidden max-w-32 truncate font-medium sm:inline">
              {activeOrgUnit.name}
            </span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Switch Organization
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {memberships.map((membership) => (
            <DropdownMenuItem
              key={membership.id}
              onClick={() => void handleSwitch(membership.org_unit_id)}
              className="cursor-pointer gap-3"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs">
                  {getInitials(membership.org_unit_name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{membership.org_unit_name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {membership.roles.map((r) => r.role_name).join(", ")}
                </p>
              </div>
              {activeOrgUnit.id === membership.org_unit_id && (
                <Check className="h-4 w-4 text-emerald-500" />
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="gap-2" onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4" />
            Create Organization
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateOrgModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onOrgCreated={() => void refreshSession()}
      />
    </>
  );
}
